import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, User, Mail, Phone, Briefcase, Lock, Sparkles, ArrowLeft } from "lucide-react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", business_name: "", password: "" });

  useEffect(() => {
    // If already logged in as client, bounce to dashboard
    if (localStorage.getItem("koko_client_token")) navigate("/dashboard", { replace: true });
  }, [navigate]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/client/login" : "/client/signup";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : form;
      const { data } = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem("koko_client_token", data.access_token);
      localStorage.setItem("koko_client_refresh", data.refresh_token);
      localStorage.setItem("koko_client_user", JSON.stringify(data.user));
      toast.success(mode === "login" ? `Welcome back, ${data.user.name}!` : `Account created! Welcome, ${data.user.name}.`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setLoading(false);
  };

  return (
    <>
      <Helmet>
        <title>{`${mode === "login" ? "Sign In" : "Create Account"} — KoKo Sites`}</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="min-h-[100dvh] bg-[#0A0A0A] flex items-center justify-center px-4 py-12 relative overflow-hidden">
        {/* Background grid decoration */}
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-1 bg-red-600" />

        <div className="relative w-full max-w-md">
          <Link to="/" className="tap inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mb-8" data-testid="back-to-home">
            <ArrowLeft size={14} /> Back to site
          </Link>

          <div className="bg-[#111] border border-white/10 p-8 sm:p-10 relative">
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-red-600" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-red-600" />

            <div className="mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-600/10 border border-red-600/30 mb-4">
                <Sparkles size={12} className="text-red-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-500">Client Portal</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-white leading-none">
                {mode === "login" ? (<>Welcome<br/><span className="text-red-600">Back.</span></>) : (<>Let's Build<br/><span className="text-red-600">Something.</span></>)}
              </h1>
              <p className="text-sm text-zinc-500 mt-4">
                {mode === "login" ? "Log in to track your orders and projects." : "Create your account to place orders and track progress."}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="client-auth-form">
              {mode === "signup" && (
                <>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><User size={11} /> Your Name *</label>
                    <input value={form.name} onChange={e => update("name", e.target.value)} required autoComplete="name" enterKeyHint="next"
                      data-testid="client-signup-name"
                      placeholder="Rahul Sharma"
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Phone size={11} /> Phone / WhatsApp *</label>
                    <input value={form.phone} onChange={e => update("phone", e.target.value)} required type="tel" inputMode="tel" autoComplete="tel" enterKeyHint="next"
                      data-testid="client-signup-phone"
                      placeholder="+91 98765 43210"
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Briefcase size={11} /> Business Name <span className="text-zinc-700 normal-case">(optional)</span></label>
                    <input value={form.business_name} onChange={e => update("business_name", e.target.value)} autoComplete="organization" enterKeyHint="next"
                      placeholder="Sharma Dental Clinic"
                      className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
                  </div>
                </>
              )}
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Mail size={11} /> Email *</label>
                <input value={form.email} onChange={e => update("email", e.target.value)} required type="email" inputMode="email" autoComplete="email" enterKeyHint="next"
                  data-testid="client-auth-email"
                  placeholder="rahul@gmail.com"
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5"><Lock size={11} /> Password *</label>
                <input value={form.password} onChange={e => update("password", e.target.value)} required type="password" minLength={6}
                  autoComplete={mode === "login" ? "current-password" : "new-password"} enterKeyHint="go"
                  data-testid="client-auth-password"
                  placeholder={mode === "login" ? "Enter password" : "Min 6 characters"}
                  className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-white focus:border-red-600 outline-none transition-colors" />
              </div>

              <button
                type="submit" disabled={loading}
                data-testid="client-auth-submit"
                className="btn-brutal btn-shine w-full bg-red-600 text-white font-bold uppercase tracking-widest py-4 px-6 border-2 border-white hover:bg-white hover:text-black text-sm disabled:opacity-60 flex items-center justify-center gap-2 mt-6"
              >
                {loading ? <><Loader2 size={14} className="animate-spin" /> Please wait...</>
                  : <>{mode === "login" ? "Sign In" : "Create Account"} <ArrowRight size={14} /></>}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-white/5 text-center">
              <p className="text-xs text-zinc-500">
                {mode === "login" ? "New to KoKo Sites?" : "Already have an account?"}{" "}
                <button
                  onClick={() => setMode(mode === "login" ? "signup" : "login")}
                  className="tap text-red-500 hover:text-white font-bold uppercase tracking-wider transition-colors"
                  data-testid="toggle-auth-mode"
                >
                  {mode === "login" ? "Create one →" : "Sign in →"}
                </button>
              </p>
            </div>
          </div>

          <p className="text-center text-[10px] text-zinc-600 mt-6 uppercase tracking-widest">
            Admin? <Link to="/admin/login" className="text-zinc-400 hover:text-red-500 transition-colors">Admin Portal →</Link>
          </p>
        </div>
      </div>
    </>
  );
}
